import type { PlayerCallbacks, PlayerInstance, DebugStats, LevelInfo, BitrateDataPoint } from "./player-types";
import { createEmptyDebugStats } from "./player-types";

/**
 * Safari native HLS player with AirPlay support and stall recovery.
 * Uses the browser's built-in HLS support for better AirPlay compatibility.
 */
export class SafariPlayer implements PlayerInstance {
  private video: HTMLVideoElement;
  private callbacks: PlayerCallbacks;

  private liveSyncInterval: number | null = null;
  private stallWatchdogInterval: number | null = null;

  private debugStats: DebugStats = createEmptyDebugStats();

  // Stall detection state
  private lastCurrentTime = 0;
  private stallStartTime: number | null = null;
  private stallRecoveryAttempts = 0;

  // Telemetry state (to match hls.js metrics)
  private manifestLevels: LevelInfo[] = [];
  private estimatedBandwidth = 0;
  private bitrateHistory: BitrateDataPoint[] = [];
  private debugStartTime = 0;
  private lastBufferEnd = 0;
  private lastBufferCheckTime = 0;
  private lastVideoHeight = 0;

  private readonly LIVE_SYNC_INTERVAL_MS = 500;
  private readonly STALL_CHECK_INTERVAL_MS = 1000;
  private readonly STALL_THRESHOLD_MS = 2000;
  private readonly MAX_STALL_RECOVERY_ATTEMPTS = 3;
  private readonly BITRATE_HISTORY_MAX_POINTS = 120;

  constructor(video: HTMLVideoElement, callbacks: PlayerCallbacks) {
    this.video = video;
    this.callbacks = callbacks;
  }

  setup(): void {
    this.debugStartTime = performance.now();

    this.video.src = this.callbacks.getStreamUrl();

    this.video.addEventListener("loadedmetadata", this.handleLoadedMetadata);
    this.video.addEventListener("error", this.handleError);
    this.video.addEventListener("playing", this.handlePlaying);
    this.video.addEventListener("ended", this.handleEnded);
    this.video.addEventListener("resize", this.handleResize);

    this.startLiveSync();
    this.startStallWatchdog();

    void this.parseManifestLevels();
  }

  cleanup(): void {
    this.video.removeEventListener("loadedmetadata", this.handleLoadedMetadata);
    this.video.removeEventListener("error", this.handleError);
    this.video.removeEventListener("playing", this.handlePlaying);
    this.video.removeEventListener("ended", this.handleEnded);
    this.video.removeEventListener("resize", this.handleResize);

    this.stopLiveSync();
    this.stopStallWatchdog();

    // iOS-specific: Force audio context shutdown before clearing source
    this.video.pause();
    this.video.muted = true;

    this.video.src = "";
    this.video.load();

    this.debugStats = createEmptyDebugStats();
    this.stallRecoveryAttempts = 0;
    this.stallStartTime = null;

    // Reset telemetry state
    this.manifestLevels = [];
    this.estimatedBandwidth = 0;
    this.bitrateHistory = [];
    this.lastBufferEnd = 0;
    this.lastBufferCheckTime = 0;
    this.lastVideoHeight = 0;
  }

  getDebugStats(): DebugStats {
    this.updateDebugStats();
    return this.debugStats;
  }

  async safePlay(): Promise<void> {
    try {
      await this.video.play();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          this.callbacks.onStateChange("autoplay-blocked");
          return;
        }
        // AbortError: play() interrupted by source change - expected during cleanup/reinitialization
        if (error.name === "AbortError") {
          return;
        }
      }
      throw error;
    }
  }

  getLiveEdge(): number | null {
    if (this.video.seekable && this.video.seekable.length) {
      return this.video.seekable.end(this.video.seekable.length - 1);
    }
    return null;
  }

  private jumpToLive(): void {
    const edge = this.getLiveEdge();
    if (edge !== null) {
      this.video.currentTime = edge;
    }
  }

  // ==================== Event Handlers ====================

  private handleLoadedMetadata = (): void => {
    this.detectAspectRatio();
    this.callbacks.onStateChange("playing");

    // Safari needs a small delay before seekable ranges exist
    setTimeout(() => {
      this.jumpToLive();
      void this.safePlay();
    }, 200);
  };

  private handlePlaying = (): void => {
    this.callbacks.onStateChange("playing");
    // Reset stall state on successful playback
    this.stallStartTime = null;
    this.stallRecoveryAttempts = 0;
  };

  private handleEnded = (): void => {
    this.callbacks.onStreamEnded();
  };

  private handleError = (): void => {
    const error = this.video.error;

    // Stream not available yet - start polling
    if (error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED || error?.code === MediaError.MEDIA_ERR_NETWORK) {
      this.cleanup();
      this.callbacks.onWaitForStream();
      return;
    }

    // Actual error
    const message =
      error?.code === MediaError.MEDIA_ERR_DECODE
        ? "Video decode error. Try refreshing."
        : "Failed to load video stream";
    this.callbacks.onError(message);
  };

  private handleResize = (): void => {
    this.trackBitrateHistory();
  };

  // ==================== Live Sync ====================

  private startLiveSync(): void {
    if (this.liveSyncInterval !== null) return;

    this.liveSyncInterval = window.setInterval(() => {
      if (this.video.paused) return;

      const edge = this.getLiveEdge();
      if (edge === null) return;

      const drift = edge - this.video.currentTime;

      if (drift > 2) {
        // Too far behind - seek to live edge
        this.video.currentTime = edge - 0.5;
        this.video.playbackRate = 1;
      } else if (drift > 1) {
        // Slightly behind - speed up to catch up
        this.video.playbackRate = 1.2;
      } else {
        this.video.playbackRate = 1;
      }
    }, this.LIVE_SYNC_INTERVAL_MS);
  }

  private stopLiveSync(): void {
    if (this.liveSyncInterval !== null) {
      clearInterval(this.liveSyncInterval);
      this.liveSyncInterval = null;
    }
  }

  // ==================== Stall Detection & Recovery ====================

  private startStallWatchdog(): void {
    if (this.stallWatchdogInterval !== null) return;

    this.lastCurrentTime = this.video.currentTime;

    this.stallWatchdogInterval = window.setInterval(() => {
      this.checkForStall();
    }, this.STALL_CHECK_INTERVAL_MS);
  }

  private stopStallWatchdog(): void {
    if (this.stallWatchdogInterval !== null) {
      clearInterval(this.stallWatchdogInterval);
      this.stallWatchdogInterval = null;
    }
  }

  private checkForStall(): void {
    // Don't check if video is paused or ended
    if (this.video.paused || this.video.ended) {
      this.stallStartTime = null;
      return;
    }

    const currentTime = this.video.currentTime;
    const timeAdvanced = Math.abs(currentTime - this.lastCurrentTime) > 0.01;

    if (timeAdvanced) {
      // Video is progressing - reset stall state
      this.lastCurrentTime = currentTime;
      this.stallStartTime = null;
      this.stallRecoveryAttempts = 0;
      return;
    }

    // Video hasn't advanced
    const now = performance.now();

    if (this.stallStartTime === null) {
      // Start tracking stall
      this.stallStartTime = now;
      return;
    }

    const stallDuration = now - this.stallStartTime;

    if (stallDuration >= this.STALL_THRESHOLD_MS) {
      // Stall detected - attempt recovery
      this.recoverFromStall();
    }
  }

  private recoverFromStall(): void {
    this.stallRecoveryAttempts++;
    this.debugStats = {
      ...this.debugStats,
      stallCount: (this.debugStats.stallCount ?? 0) + 1,
    };

    console.warn(
      `Safari HLS stall detected. Recovery attempt ${this.stallRecoveryAttempts}/${this.MAX_STALL_RECOVERY_ATTEMPTS}`,
    );

    if (this.stallRecoveryAttempts <= this.MAX_STALL_RECOVERY_ATTEMPTS) {
      // Try seeking to live edge
      const edge = this.getLiveEdge();
      if (edge !== null) {
        this.video.currentTime = edge - 0.5;
        void this.video.play().catch(() => {});
      }
      this.stallStartTime = null;
    } else {
      // Max attempts reached - full reload
      console.warn("Safari HLS: Max stall recovery attempts reached. Reloading source.");
      this.stallRecoveryAttempts = 0;
      this.stallStartTime = null;

      const streamUrl = this.callbacks.getStreamUrl();
      this.video.src = "";
      this.video.load();
      this.video.src = streamUrl;
      void this.video.play().catch(() => {});
    }
  }

  // ==================== Aspect Ratio Detection ====================

  private detectAspectRatio(): void {
    const checkAndNotify = (source: string): boolean => {
      const width = this.video.videoWidth;
      const height = this.video.videoHeight;
      console.log(
        `[SafariPlayer] detectAspectRatio check (${source}): ${width}x${height}, readyState=${this.video.readyState}`,
      );

      if (width > 0 && height > 0) {
        const ratio = height > width ? "portrait" : "landscape";
        console.log(`[SafariPlayer] Detected aspect ratio: ${ratio} (${width}x${height})`);
        this.callbacks.onAspectRatioDetected(ratio);
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkAndNotify("immediate")) {
      return;
    }

    // Fallback: listen for resize event (fires when video dimensions become available)
    const onResize = (): void => {
      if (checkAndNotify("resize")) {
        this.video.removeEventListener("resize", onResize);
      }
    };
    this.video.addEventListener("resize", onResize);

    // Additional fallback: poll briefly in case events don't fire
    let pollCount = 0;
    const pollInterval = setInterval(() => {
      pollCount++;
      if (checkAndNotify(`poll-${pollCount}`)) {
        clearInterval(pollInterval);
        this.video.removeEventListener("resize", onResize);
      } else if (pollCount >= 10) {
        console.warn("[SafariPlayer] Could not detect video dimensions after 2s, defaulting to landscape");
        clearInterval(pollInterval);
        this.video.removeEventListener("resize", onResize);
      }
    }, 200);
  }

  // ==================== Telemetry (to match hls.js metrics) ====================

  private async parseManifestLevels(): Promise<void> {
    try {
      const response = await fetch(this.callbacks.getStreamUrl());
      const manifest = await response.text();

      // Parse #EXT-X-STREAM-INF lines for RESOLUTION and BANDWIDTH
      const streamInfRegex = /#EXT-X-STREAM-INF:[^\n]*BANDWIDTH=(\d+)[^\n]*RESOLUTION=(\d+)x(\d+)/g;
      let match;
      const levels: LevelInfo[] = [];

      while ((match = streamInfRegex.exec(manifest)) !== null) {
        const bandwidth = match[1];
        const height = match[3];
        if (bandwidth && height) {
          levels.push({
            index: levels.length,
            bitrate: parseInt(bandwidth, 10),
            height: parseInt(height, 10),
          });
        }
      }

      // Sort by bitrate ascending (lowest quality = level 0)
      levels.sort((a, b) => a.bitrate - b.bitrate);
      levels.forEach((level, i) => (level.index = i));

      this.manifestLevels = levels;
    } catch {
      // Manifest parsing failed - levels will remain empty
    }
  }

  private findLevelByHeight(height: number): LevelInfo | null {
    if (!this.manifestLevels.length || !height) return null;

    let closest: LevelInfo | null = null;
    let minDiff = Infinity;

    for (const level of this.manifestLevels) {
      const diff = Math.abs(level.height - height);
      if (diff < minDiff) {
        minDiff = diff;
        closest = level;
      }
    }

    return closest;
  }

  private estimateBandwidth(): void {
    if (!this.video.buffered.length) return;

    const currentBufferEnd = this.video.buffered.end(this.video.buffered.length - 1);
    const now = performance.now();

    if (this.lastBufferCheckTime > 0) {
      const bufferGrowth = currentBufferEnd - this.lastBufferEnd;
      const timePassed = (now - this.lastBufferCheckTime) / 1000;

      if (timePassed > 0 && bufferGrowth > 0) {
        const downloadRatio = bufferGrowth / timePassed;
        const currentLevel = this.findLevelByHeight(this.video.videoHeight);
        const currentBitrate = currentLevel?.bitrate ?? 2000000;

        this.estimatedBandwidth = Math.round((currentBitrate * downloadRatio) / 1000);
      }
    }

    this.lastBufferEnd = currentBufferEnd;
    this.lastBufferCheckTime = now;
  }

  private trackBitrateHistory(): void {
    const height = this.video.videoHeight;
    if (!height || height === this.lastVideoHeight) return;

    this.lastVideoHeight = height;
    const level = this.findLevelByHeight(height);

    if (level) {
      this.bitrateHistory.push({
        time: (performance.now() - this.debugStartTime) / 1000,
        bitrate: Math.round(level.bitrate / 1000),
        ewma: this.estimatedBandwidth,
        level: level.index,
      });

      if (this.bitrateHistory.length > this.BITRATE_HISTORY_MAX_POINTS) {
        this.bitrateHistory.shift();
      }
    }
  }

  // ==================== Debug Stats ====================

  private updateDebugStats(): void {
    // Update bandwidth estimate
    this.estimateBandwidth();

    // Get dropped frames
    let droppedFrames = 0;
    const quality = this.video.getVideoPlaybackQuality?.();
    if (quality) {
      droppedFrames = quality.droppedVideoFrames ?? 0;
    } else if ("webkitDroppedFrameCount" in this.video) {
      droppedFrames =
        (this.video as HTMLVideoElement & { webkitDroppedFrameCount?: number }).webkitDroppedFrameCount ?? 0;
    }

    // Detect current level from video dimensions
    const currentLevel = this.findLevelByHeight(this.video.videoHeight);

    this.debugStats = {
      ...this.debugStats,
      droppedFrames,
      // Map stallCount to bufferStalledErrors for telemetry consistency
      bufferStalledErrors: this.debugStats.stallCount ?? 0,
      // Bandwidth estimate from buffer growth
      bandwidthEstimate: this.estimatedBandwidth,
      // Current level from video dimensions
      currentLevel: currentLevel?.index ?? -1,
      // Levels from parsed manifest
      levels: this.manifestLevels,
      // Bitrate history from dimension changes
      bitrateHistory: this.bitrateHistory,
    };
  }
}
